import { cloneDeep, toInteger } from 'lodash';
import { rotatePoint, transferPointToInt, translatePointAngle, translatePointMove } from '@/utils/math-kit';
import { IImageLayerProps, ILayerProps, IPoint, ISize } from '@/version-types';

export interface IAdjustCropImgPositionProps {
    mainLayerSize: ISize; // 外层大小
    adjustLayerSize: ISize; // 调整的图层大小
    adjustLayerRotate: number; // 调整的图层旋转角度
    adjustLayerRelativePosition: IPoint; // 图层相对于外层的位置
    ignoreScaleLimit?: boolean; // 临时增加的 HACK 字段，是否忽略缩放限制
}

export const getCropRectInfo = (id) => {
    const cropNode = document.getElementById(`${id}-crop`);
    const cropRect = cropNode?.getBoundingClientRect();
    const wrapNode = document.getElementById(`cropImageOperateNode`);
    const wrapRect = wrapNode?.getBoundingClientRect();
    return {
        cropRect,
        wrapRect,
    };
};

/**
 * 裁剪回弹
 *
 * @param props - 裁剪属性
 */
export function adjustCropImgPosition({
    mainLayerSize,
    adjustLayerSize,
    adjustLayerRotate,
    adjustLayerRelativePosition,
    ignoreScaleLimit = false,
}: IAdjustCropImgPositionProps) {
    const { width: mainLayerWidth, height: mainLayerHeight } = mainLayerSize;
    const { width: layerWidth, height: layerHeight } = adjustLayerSize;
    const { x: offsetX, y: offsetY } = adjustLayerRelativePosition;

    // 外框 相对于 图层的位置
    const mainLayerLeftTopPoint = transferPointToInt(
        translatePointMove(
            translatePointAngle(
                {
                    x: -mainLayerWidth / 2,
                    y: mainLayerHeight / 2,
                },
                adjustLayerRotate,
            ),
            offsetX,
            offsetY,
        ),
    );

    const mainLayerRightTopPoint = transferPointToInt(
        translatePointMove(
            translatePointAngle(
                {
                    x: mainLayerWidth / 2,
                    y: mainLayerHeight / 2,
                },
                adjustLayerRotate,
            ),
            offsetX,
            offsetY,
        ),
    );
    const mainLayerLeftBottomPoint = transferPointToInt(
        translatePointMove(
            translatePointAngle(
                {
                    x: -mainLayerWidth / 2,
                    y: -mainLayerHeight / 2,
                },
                adjustLayerRotate,
            ),
            offsetX,
            offsetY,
        ),
    );
    const mainLayerRightBottomPoint = transferPointToInt(
        translatePointMove(
            translatePointAngle(
                {
                    x: mainLayerWidth / 2,
                    y: -mainLayerHeight / 2,
                },
                adjustLayerRotate,
            ),
            offsetX,
            offsetY,
        ),
    );

    const halfLayerWidth = layerWidth / 2;
    const halfLayerHeight = layerHeight / 2;

    const xRange = {
        min: -halfLayerWidth,
        max: halfLayerWidth,
    };

    const yRange = {
        min: -halfLayerHeight,
        max: halfLayerHeight,
    };

    const points = [mainLayerLeftTopPoint, mainLayerRightTopPoint, mainLayerLeftBottomPoint, mainLayerRightBottomPoint];

    const overflowX = {
        max: points[0].x - xRange.max,
        min: points[0].x - xRange.min,
    };

    const overflowY = {
        max: points[0].y - yRange.max,
        min: points[0].y - yRange.min,
    };

    // 算出是否超出边界
    points.forEach(({ x, y }) => {
        const cutMaxX = x - xRange.max;
        const cutMinX = x - xRange.min;

        const cutMaxY = y - yRange.max;
        const cutMinY = y - yRange.min;

        overflowX.max = cutMaxX > overflowX.max ? cutMaxX : overflowX.max;
        overflowX.min = cutMinX < overflowX.min ? cutMinX : overflowX.min;

        overflowY.max = cutMaxY > overflowY.max ? cutMaxY : overflowY.max;
        overflowY.min = cutMinY < overflowY.min ? cutMinY : overflowY.min;
    });

    let scaleX = 1;
    let scaleY = 1;

    // 超出边界能否移回来 移不回来就要放大
    if (
        (overflowX.max > 0 && overflowX.min < 0) || // 两边都超出
        (overflowX.max > 0 && overflowX.max - overflowX.min > 0) || // 右边超出太多 回弹还是会超出
        (overflowX.min < 0 && overflowX.min - overflowX.max < 0) // 左边超出  回弹还是会超出
    ) {
        let scaleDistance = 0;
        if (overflowX.max > 0 && overflowX.min < 0) {
            scaleDistance = Math.max(overflowX.max, Math.abs(overflowX.min));
        } else if (overflowX.max > 0 && overflowX.max - overflowX.min > 0) {
            scaleDistance = overflowX.max - overflowX.min;
        } else if (overflowX.min < 0 && overflowX.min - overflowX.max < 0) {
            scaleDistance = Math.abs(overflowX.max - overflowX.min);
        }

        scaleX = 1 + (toInteger(scaleDistance) * 2) / layerWidth;
    }

    if (
        (overflowY.max > 0 && overflowY.min < 0) || // 两边都超出
        (overflowY.max > 0 && overflowY.max - overflowY.min > 0) || // 右边超出太多 回弹还是会超出
        (overflowY.min < 0 && overflowY.min - overflowY.max < 0) // 左边超出  回弹还是会超出
    ) {
        let scaleDistance = 0;
        if (overflowY.max > 0 && overflowY.min < 0) {
            scaleDistance = Math.max(overflowY.max, Math.abs(overflowY.min));
        } else if (overflowY.max > 0 && overflowY.max - overflowY.min > 0) {
            scaleDistance = overflowY.max - overflowY.min;
        } else if (overflowY.min < 0 && overflowY.min - overflowY.max < 0) {
            scaleDistance = Math.abs(overflowY.min - overflowY.max);
        }

        scaleY = 1 + (toInteger(scaleDistance) * 2) / layerHeight;
    }

    const scale = scaleX > scaleY ? scaleX : scaleY;

    let movePoint = {
        x: 0,
        y: 0,
    };

    // HACK: 移动图层位置，理论上不应该会存在 scale 的变化，针对移动图片时，不执行缩放的判断，来临时解决移动主图没有吸附到边缘的问题
    // @see https://jira.meitu.com/browse/MTXXPRO-53748
    if (scale === 1 || ignoreScaleLimit) {
        if (overflowX.max > 0) {
            movePoint.x = overflowX.max;
        } else if (overflowX.min < 0) {
            movePoint.x = overflowX.min;
        }

        if (overflowY.max > 0) {
            movePoint.y = overflowY.max;
        } else if (overflowY.min < 0) {
            movePoint.y = overflowY.min;
        }
    }

    movePoint = translatePointAngle(movePoint, -adjustLayerRotate);

    return {
        scale,
        movePoint,
    };
}

export const adjustCropLayer = (layer: ILayerProps) => {
    const { imageTransform } = layer.data as IImageLayerProps;
    const { widthRatio, scale, heightRatio, rotate, offsetX, offsetY } = imageTransform;
    const { width, height } = layer;
    const imgWidth = widthRatio * width * scale;
    const imgHeight = heightRatio * height * scale;
    const offsetDistanceX = offsetX * imgWidth;
    const offsetDistanceY = offsetY * imgHeight;
    const adjustLayerRelativePosition = rotatePoint(
        {
            x: -toInteger(offsetDistanceX),
            y: toInteger(offsetDistanceY),
        },
        rotate,
    );
    const adjustProps = {
        mainLayerSize: {
            width,
            height,
        },
        adjustLayerSize: {
            width: imgWidth,
            height: imgHeight,
        },
        adjustLayerRotate: rotate,
        adjustLayerRelativePosition,
    };
    const { movePoint, scale: adjustScale } = adjustCropImgPosition(adjustProps);

    if (adjustScale !== 1) {
        imageTransform.scale = imageTransform.scale * adjustScale;
        imageTransform.offsetX = offsetDistanceX / (imgWidth * adjustScale);
        imageTransform.offsetY = offsetDistanceY / (imgHeight * adjustScale);
    } else {
        imageTransform.offsetX = (offsetDistanceX + movePoint.x) / imgWidth;
        imageTransform.offsetY = (offsetDistanceY - movePoint.y) / imgHeight;
    }
};
